'use client';

import React, { useState } from 'react';
import Button from '@/components/ui/Button';

const CostEstimator = () => {
    const [petType, setPetType] = useState<string>('dog');

    const costData: Record<string, any> = {
        dog: {
            initial: { adoption: 300, spayNeuter: 200, supplies: 150, microchip: 50 },
            monthly: { food: 60, vet: 50, insurance: 40, toys: 20 },
        },
        cat: {
            initial: { adoption: 150, spayNeuter: 150, supplies: 100, microchip: 50 },
            monthly: { food: 40, vet: 30, insurance: 25, litter: 20 },
        },
        bird: {
            initial: { adoption: 50, cage: 200, supplies: 100, vetExam: 80 },
            monthly: { food: 20, vet: 20, toys: 15 },
        },
        reptile: {
            initial: { adoption: 75, tankSetup: 300, lighting: 100, supplies: 50 },
            monthly: { food: 25, bulbs: 10, vet: 20 },
        },
    };

    const currentCosts = costData[petType] || costData.dog;

    const calculateTotal = (obj: Record<string, number>) => Object.values(obj).reduce((a, b) => a + b, 0);

    const initialTotal = calculateTotal(currentCosts.initial);
    const monthlyTotal = calculateTotal(currentCosts.monthly);
    const annualTotal = monthlyTotal * 12;

    return (
        <div className="space-y-8">
            <div>
                <label className="block text-sm font-medium mb-3">Select Pet Type</label>
                <div className="grid grid-cols-4 gap-2">
                    {['dog', 'cat', 'bird', 'reptile'].map((type) => (
                        <button
                            key={type}
                            onClick={() => setPetType(type)}
                            className={`p-3 rounded-lg border capitalize text-sm font-medium transition-all ${petType === type
                                    ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                                    : 'border-border hover:bg-muted'
                                }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="p-5 rounded-xl bg-muted/30 border border-border">
                    <h4 className="font-semibold text-lg mb-4 text-foreground">Initial One-Time Costs</h4>
                    <div className="space-y-2 mb-4 text-sm">
                        {Object.entries(currentCosts.initial).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                                <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                <span className="font-medium">${value as number}</span>
                            </div>
                        ))}
                    </div>
                    <div className="pt-4 border-t border-border flex justify-between items-center">
                        <span className="font-bold">Total Initial</span>
                        <span className="text-xl font-bold text-brand-primary">${initialTotal}</span>
                    </div>
                </div>

                <div className="p-5 rounded-xl bg-muted/30 border border-border">
                    <h4 className="font-semibold text-lg mb-4 text-foreground">Monthly Recurring</h4>
                    <div className="space-y-2 mb-4 text-sm">
                        {Object.entries(currentCosts.monthly).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                                <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                <span className="font-medium">${value as number}/mo</span>
                            </div>
                        ))}
                    </div>
                    <div className="pt-4 border-t border-border flex justify-between items-center">
                        <span className="font-bold">Total Monthly</span>
                        <span className="text-xl font-bold text-brand-primary">${monthlyTotal}</span>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-accent/5 rounded-xl border border-accent/20 text-center">
                <p className="text-muted-foreground mb-1">Estimated First Year Cost</p>
                <p className="text-3xl font-bold text-foreground">
                    ${initialTotal + annualTotal}
                </p>
            </div>
        </div>
    );
};

export default CostEstimator;
